import type { CSSProperties } from 'react';
import {
  Anchor,
  Archive,
  Asterisk,
  AudioLines,
  Bell,
  Bird,
  CalendarCheck,
  CassetteTape,
  Castle,
  Clapperboard,
  Clover,
  Cog,
  Coins,
  Copy,
  Crown,
  Diamond,
  Dices,
  Disc3,
  Drum,
  Eye,
  Fan,
  Feather,
  Fish,
  Flame,
  FlameKindling,
  FlaskConical,
  Flower2,
  Gem,
  Hammer,
  Handshake,
  Hourglass,
  Lamp,
  Landmark,
  Leaf,
  Medal,
  Orbit,
  Rabbit,
  Ribbon,
  Shell,
  Skull,
  Snowflake,
  Sparkles,
  Sprout,
  Stamp,
  Store,
  Swords,
  Tornado,
  VenetianMask,
  Waves,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import type { EquippedCosmetics } from '@anidex/shared/economy';
import { unlockLabel } from '../../i18n/catalog';
import { AVATAR_FX_NODES, BADGE_LOOKS, BANNER_FX_NODES, FRAME_NODES } from './looks';

const ICONS: Record<string, LucideIcon> = {
  Anchor, Archive, Asterisk, AudioLines, Bell, Bird, CalendarCheck, CassetteTape, Castle, Clapperboard, Clover, Cog,
  Coins, Copy, Crown, Diamond, Dices, Disc3, Drum, Eye, Fan, Feather, Fish, Flame, FlameKindling, FlaskConical,
  Flower2, Gem, Hammer, Handshake, Hourglass, Lamp, Landmark, Leaf, Medal, Orbit, Rabbit, Ribbon, Shell, Skull,
  Snowflake, Sparkles, Sprout, Stamp, Store, Swords, Tornado, VenetianMask, Waves, Zap,
};

function nodes(count: number) {
  return Array.from({ length: count }, (_, index) => <i key={index} />);
}

function known<T extends object>(table: T, key: string | null | undefined): key is Extract<keyof T, string> {
  return Boolean(key) && Object.prototype.hasOwnProperty.call(table, key as string);
}

export function TitlePlate({
  titleKey,
  label,
  size = 'inline',
}: {
  titleKey?: string | null;
  label: string;
  size?: 'inline' | 'showcase';
}) {
  const style = titleKey ? `title-plate--${titleKey}` : 'title-plate--plain';
  return (
    <span className={`title-plate ${style}${size === 'showcase' ? ' title-plate--showcase' : ''}`}>
      <span className="title-plate__text" data-text={label}>
        {label}
      </span>
    </span>
  );
}

export function BadgeEmblem({ badgeKey, size = 28 }: { badgeKey: string; size?: number }) {
  if (!known(BADGE_LOOKS, badgeKey)) return null;
  const look = BADGE_LOOKS[badgeKey];
  const Icon = ICONS[look.icon] ?? Sparkles;
  const [rim, light, metal, deep, ink] = look.palette;
  const style = {
    width: size,
    height: size,
    '--rim': rim,
    '--light': light,
    '--metal': metal,
    '--deep': deep,
    '--ink': ink,
  } as CSSProperties;
  return (
    <span className={`badge-emblem badge-emblem--${look.shape}`} style={style} aria-hidden="true">
      {look.shape === 'medal' ? nodes(2) : null}
      <Icon size={Math.max(10, Math.round(size * 0.46))} strokeWidth={2.4} />
    </span>
  );
}

export function FrameRing({ frameKey }: { frameKey: string }) {
  if (!known(FRAME_NODES, frameKey)) return null;
  return (
    <span className={`frame-ring frame-ring--${frameKey}`} aria-hidden="true">
      <span className="frame-ring__band" />
      {nodes(FRAME_NODES[frameKey][0])}
    </span>
  );
}

export function FrameBorder({ frameKey }: { frameKey: string }) {
  if (!known(FRAME_NODES, frameKey)) return null;
  return (
    <span className={`frame-border frame-border--${frameKey}`} aria-hidden="true">
      {nodes(FRAME_NODES[frameKey][1])}
    </span>
  );
}

export function AvatarFxBack({ fxKey }: { fxKey: string }) {
  if (!known(AVATAR_FX_NODES, fxKey) || AVATAR_FX_NODES[fxKey][1] === 0) return null;
  return (
    <span className={`afx afx--back afx--${fxKey}`} aria-hidden="true">
      {nodes(AVATAR_FX_NODES[fxKey][1])}
    </span>
  );
}

export function AvatarFx({ fxKey }: { fxKey: string }) {
  if (!known(AVATAR_FX_NODES, fxKey)) return null;
  const [inside, , front] = AVATAR_FX_NODES[fxKey];
  return (
    <span className={`afx afx--front afx--${fxKey}`} aria-hidden="true">
      <span className="afx__in">{nodes(inside)}</span>
      {front > 0 ? <span className="afx__out">{nodes(front)}</span> : null}
    </span>
  );
}

export function BannerFx({ fxKey }: { fxKey: string }) {
  if (!known(BANNER_FX_NODES, fxKey)) return null;
  return (
    <span className={`bfx bfx--${fxKey}`} aria-hidden="true">
      {nodes(BANNER_FX_NODES[fxKey])}
    </span>
  );
}

export function WornMarks({ cosmetics, title }: { cosmetics?: EquippedCosmetics; title?: string | null }) {
  if (!cosmetics) return null;
  const label = cosmetics.title ? unlockLabel('title', cosmetics.title, title ?? '') : null;
  if (!label && cosmetics.badges.length === 0) return null;
  return (
    <span className="worn-marks">
      {cosmetics.badges.map((key) => (
        <BadgeEmblem key={key} badgeKey={key} size={18} />
      ))}
      {label ? <TitlePlate titleKey={cosmetics.title} label={label} /> : null}
    </span>
  );
}
